import { Effect, Fiber, Layer, Schema } from 'effect';

import {
  makeParseCheckpointStoreService,
  makeParseEventSinkService,
  makeParseSourceService,
  ParseCheckpointStore,
  ParseEventSink,
  ParseRuntimePortError,
  ParseSource,
  runParseProgramEffect,
  type ParseCheckpoint,
  type ParseRunEvent,
  type ParseRunStatus,
  type ParseRuntimeServices,
} from './parse-runtime';
import {
  ParserDefinitionSpecSchema,
  ParsedPageSchema,
  parserLayer,
  runParseDocument,
  runParsePage,
  type ParsePageInput,
  type ParserDefinition,
} from './parser-layer';
import { toCanonicalParseResult, toOkraParseOutput } from './projections/okra-parse-output';
import { parserHostMemory } from './hosts/memory';
import {
  DocumentSource,
  ParserHttp,
  PdfTextLayer,
  type ParserDependencyError,
} from './parser-host';
import type { OfflineParser } from './parser-layer';

export interface ParserConformanceFixtures {
  readonly document: ParsePageInput['document'];
  readonly expectedPageCount: number;
  readonly samplePage?: number;
  readonly options?: Readonly<Record<string, unknown>>;
}

export interface ParserConformanceResult {
  readonly pages: number;
  readonly events: readonly ParseRunEvent[];
  readonly checkpoint: ParseCheckpoint & { readonly status?: ParseRunStatus };
}

function assertContract(condition: boolean, message: string): asserts condition {
  if (!condition) throw new Error(message);
}

/**
 * Reusable layer-parameterized parser contract. Test packages invoke this with
 * memory fixtures; no assertion framework or network access is embedded here.
 */
export async function parserConformanceSuite<R, RH>(
  name: string,
  definition: ParserDefinition<R>,
  host: Layer.Layer<RH> & ([R] extends [RH] ? unknown : never),
  fixtures: ParserConformanceFixtures,
): Promise<ParserConformanceResult> {
  await Effect.runPromise(Schema.decodeUnknownEffect(ParserDefinitionSpecSchema)(definition.spec));

  const available = await Effect.runPromise(
    definition.available.pipe(Effect.provide(host)) as Effect.Effect<boolean>,
  );
  assertContract(typeof available === 'boolean', `${name}: available must resolve to a boolean`);

  const samplePage = fixtures.samplePage ?? 1;
  const page = await Effect.runPromise(
    runParsePage(
      definition,
      {
        document: fixtures.document,
        page: samplePage,
        ...(fixtures.options === undefined ? {} : { options: fixtures.options }),
      },
      host,
    ),
  );
  const decodedPage = await Effect.runPromise(Schema.decodeUnknownEffect(ParsedPageSchema)(page));
  assertContract(
    decodedPage.page === samplePage,
    `${name}: page numbers must be 1-based and stable`,
  );

  const canonical = toCanonicalParseResult([page], definition.spec, { durationMs: 0 });
  const firstProjection = JSON.stringify(toOkraParseOutput(canonical));
  const secondProjection = JSON.stringify(toOkraParseOutput(canonical));
  assertContract(
    firstProjection === secondProjection,
    `${name}: output projection must be deterministic`,
  );

  const parsed = await Effect.runPromise(
    runParseDocument(
      definition,
      {
        document: fixtures.document,
        ...(fixtures.options === undefined ? {} : { options: fixtures.options }),
      },
      host,
    ),
  );
  assertContract(
    parsed.pages.length === fixtures.expectedPageCount,
    `${name}: document parse must return ${fixtures.expectedPageCount} pages`,
  );

  const interruptionFiber = Effect.runFork(
    runParsePage(
      definition,
      {
        document: fixtures.document,
        page: samplePage,
        ...(fixtures.options === undefined ? {} : { options: fixtures.options }),
      },
      host,
    ).pipe(Effect.andThen(Effect.never)),
  );
  await Effect.runPromise(Fiber.interrupt(interruptionFiber));

  // The S.5.16 parser runtime is PDF-source based. Definitions whose declared
  // input is not `pdf-bytes` (for example a GCS-prefix projection parser) are
  // still conformance-checked through spec/available/parsePage/parseDocument
  // and interruption above, but cannot be fed through parserLayer's byte
  // source without inventing a fake PDF transport.
  if (!definition.spec.inputs.includes('pdf-bytes')) {
    return {
      pages: parsed.pages.length,
      events: [],
      checkpoint: { completedPages: [], failedPages: [] },
    };
  }

  const sourceBytes = await Effect.runPromise(
    Effect.gen(function* () {
      const source = yield* DocumentSource;
      return yield* source.bytes(fixtures.document);
    }).pipe(Effect.provide(host as Layer.Layer<DocumentSource>)) as Effect.Effect<
      Uint8Array,
      ParserDependencyError
    >,
  );
  const checkpoints = new Map<string, ParseCheckpoint & { status?: ParseRunStatus }>();
  const events: ParseRunEvent[] = [];
  const source = makeParseSourceService((sourceId) =>
    sourceId === fixtures.document.documentId
      ? Effect.succeed(sourceBytes)
      : Effect.fail(
          new ParseRuntimePortError({
            port: 'source',
            operation: 'load',
            code: 'source_not_found',
            retryable: false,
          }),
        ),
  );
  const checkpoint = makeParseCheckpointStoreService({
    load: (runId) =>
      Effect.sync(() => {
        const value = checkpoints.get(runId);
        return {
          completedPages: [...(value?.completedPages ?? [])],
          failedPages: [...(value?.failedPages ?? [])],
        };
      }),
    isActive: () => Effect.succeed(true),
    recordPage: (runId, record) =>
      Effect.sync(() => {
        const value = checkpoints.get(runId) ?? { completedPages: [], failedPages: [] };
        const completedPages = [...value.completedPages];
        const failedPages = [...value.failedPages];
        if ('blocks' in record) completedPages.push(record.page);
        else failedPages.push(record.page);
        checkpoints.set(runId, {
          completedPages: [...new Set(completedPages)].sort((left, right) => left - right),
          failedPages: [...new Set(failedPages)].sort((left, right) => left - right),
          ...(value.status === undefined ? {} : { status: value.status }),
        });
      }),
    finish: (runId, status) =>
      Effect.sync(() => {
        const value = checkpoints.get(runId) ?? { completedPages: [], failedPages: [] };
        checkpoints.set(runId, { ...value, status });
      }),
  });
  const eventSink = makeParseEventSinkService((event) =>
    Effect.sync(() => {
      events.push(event);
    }),
  );
  const definitionLayer = parserLayer(definition).pipe(
    Layer.provide(host),
  ) as Layer.Layer<ParseRuntimeServices>;
  const runtimeLayer = Layer.mergeAll(
    definitionLayer,
    Layer.succeed(ParseSource, source),
    Layer.succeed(ParseCheckpointStore, checkpoint),
    Layer.succeed(ParseEventSink, eventSink),
  );
  const runId = `${name}-conformance`;
  const runtimeResult = await Effect.runPromise(
    runParseProgramEffect({
      runId,
      sourceId: fixtures.document.documentId,
      parserId: definition.spec.id,
      concurrency: 2,
    }).pipe(Effect.provide(runtimeLayer)),
  );
  assertContract(runtimeResult.pagesTotal === 2, `${name}: parserLayer must run a 2-page fixture`);
  assertContract(
    runtimeResult.pagesDone === 2,
    `${name}: both fixture pages must checkpoint (${JSON.stringify({ runtimeResult, events })})`,
  );
  assertContract(events[0]?.type === 'started', `${name}: runtime must emit started`);
  assertContract(events.at(-1)?.type === 'completed', `${name}: runtime must emit completed`);
  const finalCheckpoint = checkpoints.get(runId);
  assertContract(finalCheckpoint !== undefined, `${name}: runtime must persist a checkpoint`);
  return { pages: parsed.pages.length, events, checkpoint: finalCheckpoint };
}

// These bodies are never called; `tsc --noEmit` evaluates them as contract proofs.
function proveMemoryHostExcludesHttp(
  definition: ParserDefinition<ParserHttp>,
  input: ParsePageInput,
): void {
  // @ts-expect-error parserHostMemory intentionally cannot satisfy ParserHttp.
  runParsePage(definition, input, parserHostMemory());
}

function proveMemoryHostRunsOffline(
  definition: OfflineParser<ParserDefinition<PdfTextLayer>>,
  input: ParsePageInput,
): void {
  runParsePage(definition, input, parserHostMemory());
}

void proveMemoryHostExcludesHttp;
void proveMemoryHostRunsOffline;
